import gql from 'graphql-tag'
import {buildQuery} from 'axios-graphql-builder'

const portfolio_entries_fragment = gql`
	fragment DasboardPortfolioEntries on PortfolioEntry {
		actions
		activityType
		allInRate
		assetClassification
		assetMaturityDate
		assetSubType
		assetType
		availableQuantity
		balanceSheetContribution {
			amount
			currency
		}
		baseNotional {
			amount
			currency
		}
		basePrice {
			amount
			currency
		}
		bbId
		book
		borrowFee
		loanFee
		buySellInd
		collateralType
		collateral {
			amount
			currency
		}
		counterParty
		createdBy
		createdDate
		cusip
		effectiveBaseNotional {
			amount
			currency
		}
		endDate
		exchangeRate
		fitchRating
		fund
		haircut
		hqla
		id
		inefficiencies {
			inefficiency
			abbr
			displayName
		}
		index
		isin
		legalEntity
		maturityDate
		maturityType
		moodyRating
		needQuantity
		notional {
			amount
			currency
		}
		poolFactor
		portfolioEntryType
		partial
		price {
			amount
			currency
		}
		priceMultiplier
		quantity
		recallInefficiencyContribution {
			amount
			currency
		}
		recallQuantity
		referenceRate
		returnInefficiencyContribution {
			amount
			currency
		}
		returnQuantity
		availabilitiesContribution {
			amount
			currency
		}
		needsContribution {
			amount
			currency
		}
		originalTradeType
		platformTradeId
		rebate
		ric
		sector
		securityId
		securityIdType
		settlementDate
		snpRating
		sourceSystem
		sourceSystemTradeId
		spread
		startDate
		tenor
		termDate
		tradeDate
		ticker
		tradeType
	}
`

const dashboardEntityAmountQuery = gql`
	query {
		portfolio {
			overView {
				matureTrades {
					amount
					currency
				}
				openTrades {
					amount
					currency
				}
				availabilities {
					amount
					currency
				}
				needs {
					amount
					currency
				}
				recallInefficiencies {
					amount
					currency
				}
				returnInefficiencies {
					amount
					currency
				}
				longTrades {
					amount
					currency
				}
				shortTrades {
					amount
					currency
				}
				nonCashLoan {
					amount
					currency
				}
				nonCashBorrow {
					amount
					currency
				}
			}
		}
	}
`

const recallInefficienciesDataQuery = gql`
	query {
		portfolio {
			recallInefficiencies {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const returnInefficienciesDataQuery = gql`
	query {
		portfolio {
			returnInefficiencies {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const nonCashBorrowDataQuery = gql`
	query {
		portfolio {
			nonCashTrades {
				counterParty
				borrow {
					amount
					currency
				}
				loan {
					amount
					currency
				}
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`
const nonCashLoanDataQuery = nonCashBorrowDataQuery

const matureTradesDataQuery = gql`
	query {
		portfolio {
			matureTrades {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const openTradesDataQuery = gql`
	query {
		portfolio {
			openTrades {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const longTradesDataQuery = gql`
	query {
		portfolio {
			longTrades {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const shortTradesDataQuery = gql`
	query {
		portfolio {
			shortTrades {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const availabilitiesDataQuery = gql`
	query {
		portfolio {
			availabilities {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

const needsDataQuery = gql`
	query {
		portfolio {
			needs {
				entries {
					...DasboardPortfolioEntries
				}
			}
		}
	}
	${portfolio_entries_fragment}
`

export const tradingQueueSummaryQuery = (cptyName, cptyType) => {
	const counterParties = cptyName ? '"'.concat(cptyName.join('","'), '"') : ''
	return gql`
		query {
			tradingQueueSummary(tradingQueueSummaryRequest: {counterParties:[${counterParties}], type: "${cptyType}"}) {
				counterParty
				userSummaries {
					userEmail
					entriesCount
				}
			}
		}
	`
}

const intraDayActivitiesQuery = gql`
	query {
		portfolio {
			intraDayActivities {
				counterParty
				activityType
				tradeType
				securityId
				quantity
				price {
					amount
					currency
				}
				basePrice {
					amount
					currency
				}
				baseNotional {
					amount
					currency
				}
				createdBy
				createdDate
			}
		}
	}
`

export const tradeDetailsEntries = {
	actions: null,
	activityType: null,
	allInRate: null,
	assetMaturityDate: null,
	assetSubType: null,
	assetType: null,
	availabilitiesContribution: {
		amount: null,
	},
	balanceSheetContribution: {
		amount: null,
		currency: null,
	},
	baseNotional: {
		amount: null,
		currency: null,
	},
	bbId: null,
	book: null,
	borrowFee: null,
	buySellInd: null,
	cash: {
		amount: null,
		currency: null,
	},
	collateral: {
		amount: null,
		currency: null,
	},
	collateralType: null,
	counterParty: null,
	createdBy: null,
	createdDate: null,
	cusip: null,
	endDate: null,
	exchangeRate: null,
	fitchRating: null,
	fund: null,
	// fxRate: null,
	haircut: null,
	hqla: null,
	id: null,
	inefficiencies: {
		inefficiency: null,
		abbr: null,
		displayName: null,
	},
	index: null,
	isin: null,
	isRestricted: null,
	legalEntity: null,
	loanFee: null,
	maturityDate: null,
	maturityType: null,
	moodyRating: null,
	needsContribution: {
		amount: null,
	},
	notional: {
		amount: null,
		currency: null,
	},
	originalTradeType: null,
	partial: null,
	poolFactor: null,
	portfolioEntryType: null,
	portfolioEntryTypeOption: null,
	price: {
		amount: null,
		currency: null,
	},
	priceMultiplier: null,
	quantity: null,
	rebate: null,
	recallInefficiencyContribution: {
		amount: null,
	},
	referenceRate: null,
	returnInefficiencyContribution: {
		amount: null,
	},
	ric: null,
	sector: null,
	securityId: null,
	securityIdType: null,
	settlementDate: null,
	snpRating: null,
	sourceSystem: null,
	sourceSystemTradeId: null,
	spread: null,
	startDate: null,
	tenor: null,
	termDate: null,
	ticker: null,
	tradeDate: null,
	tradeType: null,
}
const all = {
	query: {
		portfolio: {
			all: {
				entries: tradeDetailsEntries,
			},
			longTrades: {
				entries: tradeDetailsEntries,
			},
			shortTrades: {
				entries: tradeDetailsEntries,
			},
		},
	},
}
const allTradesQuery = buildQuery(all)
const dashboardPortfolioAnalyticsQuery = {
	recallInefficienciesDataQuery,
	returnInefficienciesDataQuery,
	nonCashBorrowDataQuery,
	nonCashLoanDataQuery,
	matureTradesDataQuery,
	openTradesDataQuery,
	longTradesDataQuery,
	shortTradesDataQuery,
	availabilitiesDataQuery,
	needsDataQuery,
}

export {dashboardEntityAmountQuery, dashboardPortfolioAnalyticsQuery, allTradesQuery, intraDayActivitiesQuery}
